// Scorecards page: author one manager scorecard per call type.
//
// Layout: two columns. LEFT = the Call Type picker (a custom dropdown whose panel opens to the RIGHT
// of the button, grouped by lifecycle stage) + Save/Revert. RIGHT = a side-by-side config table per
// skill: the read-only "System Default" set (minimum boxes + weight) next to the editable "Custom"
// set (empty minimum boxes + weight), separated by a divider.
//
// Persistence uses the EXISTING template CRUD. The scorecard's name IS its key: each call type's
// scorecard is stored under the system template's canonical name (e.g. "Discovery"), so Save upserts
// by name and the per-tenant UNIQUE-name constraint gives us exactly one scorecard per call type.
// No worker or schema change is needed for this page.
//
// Trust-zone note: this file deals only with manager config (minimums/weights) — never scoring
// prompts or rubric code.

import * as api from '../lib/api.js';
import { customSelect } from '../lib/customSelect.js';
import type { Importance, ManagerScorecard, ScorecardSkill } from '../lib/types.js';
import { SKILL_ORDER, displaySkillName } from '../lib/skills.js';
import { setText } from '../lib/escape.js';

interface MyRow {
  id: string;
  template_name: string;
  updated_at: string;
}

const IMPORTANCE_WEIGHT: Record<Importance, number> = { Standard: 1.0, Important: 1.5, Critical: 2.0 };

// Shared guard: an all-optional (every minimum 0) scorecard makes every skill bonus-only, which
// inflates scores. Both save paths (this page + the Diagnostic) confirm before persisting one.
export const ALL_OPTIONAL_WARNING =
  'No skills are required, every skill will be optional, which inflates scores. Save anyway?';
export const isAllOptional = (sc: { skills: { expected_score: number }[] }): boolean =>
  sc.skills.every((s) => s.expected_score === 0);

// Lifecycle-stage grouping for the picker — LABELS + ordering only (mirrors the worker's deterministic
// call_type→stage derivation). Keyed by each system template's call_type_hint; null = the Default
// fallback template. Any hint not listed falls into a trailing "Other" group.
const STAGE_GROUPS: { stage: string; hints: (string | null)[] }[] = [
  { stage: 'Prospecting', hints: ['Cold', 'Inbound'] },
  { stage: 'Qualification', hints: ['Intro'] },
  { stage: 'Discovery', hints: ['Disc', 'Product Demo'] },
  { stage: 'Business Case', hints: ['BizCase'] },
  { stage: 'Technical Validation', hints: ['TechVal'] },
  { stage: 'Commercial', hints: ['Commercial'] },
  { stage: 'Onboarding', hints: ['Onboard'] },
  { stage: 'Adoption / Retention', hints: ['QBR', 'Adoption', 'Escalation'] },
  { stage: 'Expansion', hints: ['Expansion'] },
  { stage: 'Mid-cycle (any stage)', hints: ['Follow'] },
  { stage: 'Fallback', hints: [null] },
];

const DEFAULT_KEY = '__default__';
const hintKey = (h: string | null | undefined): string => (h == null ? DEFAULT_KEY : h);

function el(tag: string, cls?: string, text?: string): HTMLElement {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text != null) setText(n, text);
  return n;
}

/** A small gold sparkle icon — drawn beside the "Custom" tag to signal a customized call type. */
function goldSpark(): SVGElement {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('class', 'sc-opt-spark');
  svg.setAttribute('aria-hidden', 'true');
  const p = document.createElementNS(ns, 'path');
  p.setAttribute('d', 'M12 2.5l2.3 5.9 5.9 2.3-5.9 2.3L12 18.9 9.7 13 3.8 10.7 9.7 8.4z');
  svg.appendChild(p);
  return svg;
}

/** Five-cell bar with a single marker at `value` (0 = none). Read-only spans, or editable buttons. */
function bar(value: number, editable: boolean, onSet?: (v: number) => void, aria?: string): HTMLElement {
  const wrap = el('div', editable ? 'sc-bar sc-bar-edit' : 'sc-bar sc-bar-default');
  for (let i = 1; i <= 5; i++) {
    const cell = document.createElement(editable ? 'button' : 'span');
    cell.className = 'seg-cell' + (i === value ? ' seg-expected' : '');
    if (editable) {
      (cell as HTMLButtonElement).type = 'button';
      if (aria) cell.setAttribute('aria-label', `${aria}: ${i}`);
      cell.addEventListener('click', () => onSet?.(value === i ? 0 : i));
    }
    wrap.appendChild(cell);
  }
  return wrap;
}

export class ScorecardsPage {
  private readonly picker: HTMLElement;
  private readonly pickerBtn: HTMLButtonElement;
  private readonly pickerLabel: HTMLElement;
  private readonly panel: HTMLElement;
  private readonly tableRoot: HTMLElement;
  private readonly subEl: HTMLElement;
  private readonly saveBtn: HTMLButtonElement;
  private readonly revertBtn: HTMLButtonElement;
  private readonly msgEl: HTMLElement;
  private readonly ok: boolean;

  private system: ManagerScorecard[] = [];
  private mine: MyRow[] = [];
  private byKey = new Map<string, ManagerScorecard>();
  private orderedKeys: string[] = []; // call-type keys in picker order (for arrow navigation)
  private currentKey = ''; // the selected call type
  private activeKey = ''; // the keyboard-highlighted option while the panel is open
  private selected: ManagerScorecard | null = null; // system default for the selected call type
  private draft: ManagerScorecard | null = null; // the editable Custom set
  private baseline: ManagerScorecard | null = null; // persisted state Save is compared against
  private msgTimer = 0;

  constructor() {
    this.picker = document.getElementById('sc-picker')!;
    this.pickerBtn = document.getElementById('sc-select-btn') as HTMLButtonElement;
    this.pickerLabel = document.getElementById('sc-select-label')!;
    this.panel = document.getElementById('sc-select-panel')!;
    this.tableRoot = document.getElementById('sc-table')!;
    this.subEl = document.getElementById('sc-editor-sub')!;
    this.saveBtn = document.getElementById('sc-save-btn') as HTMLButtonElement;
    this.revertBtn = document.getElementById('sc-revert-btn') as HTMLButtonElement;
    this.msgEl = document.getElementById('sc-msg')!;
    this.ok = Boolean(this.picker && this.pickerBtn && this.panel && this.tableRoot && this.saveBtn);

    if (this.ok) {
      this.pickerBtn.addEventListener('click', () => this.togglePanel());
      this.pickerBtn.addEventListener('keydown', (e) => this.onBtnKey(e));
      this.saveBtn.addEventListener('click', () => void this.onSave());
      this.revertBtn.addEventListener('click', () => void this.onRevert());
    }
  }

  async init(): Promise<void> {
    if (!this.ok) return;
    try {
      const [system, mine] = await Promise.all([
        api.listSystemTemplates(),
        api.listMyTemplates().catch(() => [] as MyRow[]),
      ]);
      this.system = system;
      this.mine = mine;
    } catch {
      setText(this.subEl, 'Could not load scorecards, please reload the page.');
      return;
    }
    this.byKey = new Map(this.system.map((t) => [hintKey(t.call_type_hint), t]));
    // Flat call-type order (lifecycle order), shared by the picker list and arrow navigation.
    const seen = new Set<string>();
    this.orderedKeys = [];
    for (const group of STAGE_GROUPS) {
      for (const h of group.hints) {
        const tpl = this.byKey.get(hintKey(h));
        if (tpl) {
          this.orderedKeys.push(hintKey(tpl.call_type_hint));
          seen.add(hintKey(tpl.call_type_hint));
        }
      }
    }
    for (const t of this.system) {
      const k = hintKey(t.call_type_hint);
      if (!seen.has(k)) this.orderedKeys.push(k);
    }
    const first = this.system.find((t) => t.call_type_hint != null) ?? this.system[0];
    if (first) void this.selectKey(hintKey(first.call_type_hint));
  }

  /** Re-fetch saved scorecards and re-render the current selection. Called after a save on the
   *  Diagnostic page so this page's Custom column and CUSTOM markers reflect it without a reload. */
  async refresh(): Promise<void> {
    if (!this.ok) return;
    this.mine = await api.listMyTemplates().catch(() => this.mine);
    if (this.currentKey) await this.selectKey(this.currentKey);
  }

  /** The user's saved scorecard for a call type, matched by its canonical (system) name, if any. */
  private customFor(name: string): MyRow | undefined {
    return this.mine.find((m) => m.template_name === name);
  }

  // ---- Custom right-opening picker ----

  private togglePanel(): void {
    if (this.panel.hidden) this.openPanel();
    else this.closePanel();
  }

  private openPanel(): void {
    this.activeKey = this.currentKey;
    this.buildPickerOptions();
    this.panel.hidden = false;
    // Cap the panel so its bottom clears the footer (or the viewport, whichever is higher); it
    // scrolls internally instead of overlapping. Panel top aligns with the button top.
    const top = this.pickerBtn.getBoundingClientRect().top;
    const footer = document.querySelector('.site-footer');
    const limit = footer ? footer.getBoundingClientRect().top : window.innerHeight;
    this.panel.style.maxHeight = `${Math.max(220, limit - top - 12)}px`;
    this.pickerBtn.setAttribute('aria-expanded', 'true');
    this.highlightActive();
    document.addEventListener('mousedown', this.onOutside, true);
    document.addEventListener('keydown', this.onKey);
  }

  // Keyboard: arrows jump straight to the prev/next call type when the panel is closed (friction-free
  // exploration); when open, they move the highlight and Enter commits it.
  private onBtnKey(e: KeyboardEvent): void {
    if (!this.panel.hidden) {
      if (e.key === 'ArrowDown') { e.preventDefault(); this.moveActive(1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); this.moveActive(-1); }
      else if (e.key === 'Enter') { e.preventDefault(); this.commitActive(); }
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); this.step(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); this.step(-1); }
  }

  private step(delta: number): void {
    if (!this.orderedKeys.length) return;
    const i = Math.max(0, this.orderedKeys.indexOf(this.currentKey));
    const n = this.orderedKeys.length;
    void this.selectKey(this.orderedKeys[(i + delta + n) % n]!);
  }

  private moveActive(delta: number): void {
    if (!this.orderedKeys.length) return;
    const i = Math.max(0, this.orderedKeys.indexOf(this.activeKey));
    const n = this.orderedKeys.length;
    this.activeKey = this.orderedKeys[(i + delta + n) % n]!;
    this.highlightActive();
  }

  private highlightActive(): void {
    for (const opt of this.panel.querySelectorAll<HTMLElement>('.sc-opt')) {
      const on = opt.dataset.key === this.activeKey;
      opt.classList.toggle('is-highlighted', on);
      if (on) opt.scrollIntoView({ block: 'nearest' });
    }
  }

  private commitActive(): void {
    const key = this.activeKey;
    this.closePanel();
    this.pickerBtn.focus();
    void this.selectKey(key);
  }

  private closePanel(): void {
    this.panel.hidden = true;
    this.pickerBtn.setAttribute('aria-expanded', 'false');
    document.removeEventListener('mousedown', this.onOutside, true);
    document.removeEventListener('keydown', this.onKey);
  }

  private onOutside = (e: MouseEvent): void => {
    if (!this.picker.contains(e.target as Node)) this.closePanel();
  };

  private onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      this.closePanel();
      this.pickerBtn.focus();
    }
  };

  private buildPickerOptions(): void {
    this.panel.innerHTML = '';
    const placed = new Set<string>();
    const addOpt = (tpl: ManagerScorecard): void => {
      const key = hintKey(tpl.call_type_hint);
      let cls = 'sc-opt';
      if (key === this.currentKey) cls += ' is-active';
      if (key === this.activeKey) cls += ' is-highlighted';
      const opt = el('button', cls);
      (opt as HTMLButtonElement).type = 'button';
      opt.dataset.key = key;
      opt.setAttribute('role', 'option');
      opt.appendChild(el('span', 'sc-opt-name', tpl.template_name));
      if (this.customFor(tpl.template_name)) {
        const badge = el('span', 'sc-opt-custom', 'Custom');
        badge.appendChild(goldSpark());
        opt.appendChild(badge);
      }
      opt.addEventListener('click', () => {
        this.closePanel();
        this.pickerBtn.focus();
        void this.selectKey(key);
      });
      this.panel.appendChild(opt);
      placed.add(key);
    };
    // Flat list of call types (no stage headers), kept in lifecycle order for a sensible sequence.
    for (const group of STAGE_GROUPS) {
      const items = group.hints.map((h) => this.byKey.get(hintKey(h))).filter((t): t is ManagerScorecard => Boolean(t));
      for (const tpl of items) addOpt(tpl);
    }
    for (const tpl of this.system.filter((t) => !placed.has(hintKey(t.call_type_hint)))) addOpt(tpl);
  }

  private async selectKey(key: string): Promise<void> {
    const tpl = this.byKey.get(key);
    if (!tpl) return;
    this.currentKey = key;
    this.selected = tpl;
    setText(this.pickerLabel, tpl.template_name);

    const existing = this.customFor(tpl.template_name);
    let draft: ManagerScorecard;
    if (existing) {
      try {
        draft = await api.getTemplate(existing.id);
      } catch {
        draft = structuredClone(tpl);
      }
      if (this.currentKey !== key) return; // a newer selection (rapid arrowing) superseded this one
    } else {
      // No saved custom → the Custom column starts as a COPY of the System Default, so the two
      // columns match until the manager actually changes something (Save stays disabled until then).
      draft = structuredClone(tpl);
    }
    draft.template_name = tpl.template_name; // enforce the canonical (auto-named) key
    draft.call_type_hint = tpl.call_type_hint ?? null;
    this.draft = draft;
    // Baseline = the persisted state we edit against (the saved custom, or the system default when
    // none). Save lights up only when the Custom column differs from this.
    this.baseline = structuredClone(draft);

    this.renderTable();
    this.refreshSave();
    this.setStatus(Boolean(existing));
  }

  /** Two side-by-side sets per skill: System Default (read-only boxes + weight) and Custom (editable
   *  boxes + weight), separated by a vertical divider. */
  private renderTable(): void {
    const root = this.tableRoot;
    root.innerHTML = '';
    if (!this.selected || !this.draft) return;
    const defBySkill = new Map(this.selected.skills.map((s) => [s.skill_id, s]));
    const draftBySkill = new Map(this.draft.skills.map((s) => [s.skill_id, s]));
    const skills = SKILL_ORDER.filter((s) => defBySkill.has(s.id) && draftBySkill.has(s.id));

    // Vertical divider (column 4) spanning the header + every skill row.
    const divider = el('div', 'sc-vdivider');
    divider.style.gridColumn = '4';
    divider.style.gridRow = `1 / ${skills.length + 2}`;
    root.appendChild(divider);

    const groupHeader = (text: string, custom: boolean): HTMLElement => {
      const h = el('div', 'sc-ch' + (custom ? ' sc-ch-custom' : ''), text);
      h.style.gridColumn = 'span 2';
      return h;
    };
    root.append(el('div', 'sc-ch'), groupHeader('System Default', false), groupHeader('Custom', true));

    for (const { id, label } of skills) {
      const def = defBySkill.get(id)!;
      const cust = draftBySkill.get(id)!;

      root.appendChild(el('div', 'sc-rowlabel', label));
      root.appendChild(bar(def.expected_score, false));
      root.appendChild(el('div', 'sc-defweight', def.importance));

      const custWrap = el('div', 'sc-bar-slot');
      const drawCustom = (): void => {
        custWrap.innerHTML = '';
        custWrap.appendChild(
          bar(cust.expected_score, true, (v) => {
            cust.expected_score = v;
            cust.critical = v > 0 && cust.importance === 'Critical';
            drawCustom();
            this.refreshSave();
          }, `Set ${displaySkillName(cust.skill_name)} minimum`),
        );
      };
      drawCustom();
      root.appendChild(custWrap);

      root.appendChild(this.weightSelect(cust));
    }
  }

  private weightSelect(skill: ScorecardSkill): HTMLElement {
    const cs = customSelect({
      value: skill.importance,
      options: (['Standard', 'Important', 'Critical'] as Importance[]).map((o) => ({ id: o, name: o })),
      ariaLabel: 'Importance',
      className: 'sc-weight',
      onChange: (v) => {
        const imp = v as Importance;
        skill.importance = imp;
        skill.weight = IMPORTANCE_WEIGHT[imp];
        skill.critical = imp === 'Critical' && skill.expected_score >= 1;
        this.refreshSave();
      },
    });
    return cs.root;
  }

  /** The Custom column differs from the baseline (saved custom, or the system default when none) —
   *  i.e. there is something meaningful to Save. Matching the default (or the saved state) = not dirty. */
  private isDirty(): boolean {
    if (!this.draft || !this.baseline) return false;
    const base = new Map(this.baseline.skills.map((s) => [s.skill_id, s]));
    return this.draft.skills.some((s) => {
      const b = base.get(s.skill_id);
      return !b || s.expected_score !== b.expected_score || s.importance !== b.importance;
    });
  }

  private refreshSave(): void {
    this.saveBtn.disabled = !this.isDirty();
  }

  private setStatus(isCustom: boolean): void {
    this.revertBtn.hidden = !isCustom;
    setText(
      this.subEl,
      isCustom
        ? 'Your Custom settings apply automatically when a call of this type is scored. Adjust and Save to update them.'
        : 'This call type uses STUDIO’s system defaults. The Custom column starts matching them, change it and Save to override.',
    );
  }

  private flash(text: string, isError = false): void {
    this.msgEl.className = 'sc-msg' + (isError ? ' sc-msg-error' : ' sc-msg-ok');
    setText(this.msgEl, text);
    if (this.msgTimer) window.clearTimeout(this.msgTimer);
    this.msgTimer = window.setTimeout(() => setText(this.msgEl, ''), 3500);
  }

  private async onSave(): Promise<void> {
    if (!this.draft || !this.selected) return;
    const sc = structuredClone(this.draft);
    sc.template_name = this.selected.template_name;
    sc.call_type_hint = this.selected.call_type_hint ?? null;
    if (isAllOptional(sc) && !window.confirm(ALL_OPTIONAL_WARNING)) return;
    const existing = this.customFor(sc.template_name);
    this.saveBtn.disabled = true;
    try {
      if (existing) await api.updateTemplate(existing.id, sc);
      else await api.saveTemplate(sc);
      this.mine = await api.listMyTemplates().catch(() => this.mine);
      this.baseline = structuredClone(sc); // the saved state is the new baseline → Save greys out
      this.setStatus(true);
      this.refreshSave();
      this.flash(`Saved your ${sc.template_name} scorecard.`);
    } catch (e) {
      this.refreshSave(); // re-enable Save on failure so the user can retry
      this.flash((e as Error).message, true);
    }
  }

  private async onRevert(): Promise<void> {
    if (!this.selected) return;
    const existing = this.customFor(this.selected.template_name);
    if (!existing) return;
    try {
      await api.deleteTemplate(existing.id);
      this.mine = await api.listMyTemplates().catch(() => this.mine);
      await this.selectKey(hintKey(this.selected.call_type_hint));
      this.flash(`Reverted ${this.selected.template_name} to the system default.`);
    } catch (e) {
      this.flash((e as Error).message, true);
    }
  }
}
